import { defineStore } from 'pinia';
import { sportTitle } from '@/services/admin_actions/apiUtils';

type DismissToken = { id: string; dismissedAt: string; expiresAt: string };

export const useModalStore = defineStore('modal', {
  state: () => ({
    isVisible: false,

    // ✅ persistence identity + controls (defaults preserve current behaviour)
    id: null as string | null,
    disablePersistence: true, // default OFF so existing modals behave exactly the same
    ttlDays: 30,

    title: null as string | null,
    description: null as string | null,
    noPadding: false,
    closeIcon: false,
    maxWidth: undefined as number | string | undefined, // v-dialog max-width; defaults to 700 in ModalPopup
    narrow: false, // smaller panel from tablet up; ignored when maxWidth is set
    persistent: false, // blocks backdrop-click/ESC dismissal and hides the close icon — for must-complete modals
    component: null as any,
    componentProps: {} as Record<string, any>,
  }),
  actions: {
    openModal({
      id,
      title,
      description,
      component,
      props = {},
      options = {},
    }: {
      id?: string;
      title: string;
      description: string;
      component: any;
      props?: object;
      options?: {
        noPadding?: boolean;
        whiteIcon?: boolean;
        maxWidth?: number | string;
        narrow?: boolean;
        persistent?: boolean;

        // ✅ persistence controls (opt-in)
        disablePersistence?: boolean;
        ttlDays?: number;
      };
    }) {
      // ✅ persistence config (opt-in)
      this.id = id ?? null;
      this.disablePersistence = options.disablePersistence ?? true; // keep default OFF
      this.ttlDays = options.ttlDays ?? 30;

      this.title = title;
      this.description = description;
      // Callers should pass a markRaw'd component so Vue doesn't proxy it.
      this.component = component;
      this.componentProps = props;
      this.noPadding = options.noPadding ?? false;
      this.closeIcon = options.whiteIcon ?? false;
      this.maxWidth = options.maxWidth;
      this.narrow = options.narrow ?? false;
      this.persistent = options.persistent ?? false;
      this.isVisible = true;
    },

    closeModal() {
      // ✅ write dismissal token on close (only when explicitly enabled)
      try {
        if (!this.disablePersistence && this.id) {
          const now = new Date();
          const expires = new Date(now);
          expires.setDate(now.getDate() + (this.ttlDays ?? 30));

          const key = `modal:${sportTitle.value.toLowerCase()}:${this.id}`;
          const token: DismissToken = {
            id: this.id,
            dismissedAt: now.toISOString(),
            expiresAt: expires.toISOString(),
          };

          localStorage.setItem(key, JSON.stringify(token));
        }
      } catch {
        // fail silently
      }

      this.isVisible = false;
      this.title = '';
      this.description = '';
      this.maxWidth = undefined;
      this.narrow = false;
      this.persistent = false;
      this.component = null;
      this.componentProps = {};

      // reset persistence fields
      this.id = null;
      this.disablePersistence = true;
      this.ttlDays = 30;
    },
  },
});
